import { useTranslation } from '../../../../../shared/i18n';
import React, { useMemo, useState } from 'react';
import { Table, Input, Tag, Button, Grid } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import FilterBar from './FilterBar';
import { filterTlds } from './searchUtils';
import { formatMoney } from '../../../../../shared/utils/money';

const { useBreakpoint } = Grid;

/**
 * 价格表：全部可售后缀 + 筛选。选中语种时，先列出该语种的原生后缀（.中国），
 * 表格里是"接受该语种二级标签"的后缀。
 */
const TldPriceTable = ({ catalog, loading, filters, onFiltersChange, onPick }) => {
    const { t } = useTranslation();
    const [q, setQ] = useState('');
    const screens = useBreakpoint();
    const mobile = !screens.md;

    const list = useMemo(() => {
        let l = filterTlds(catalog, filters);
        const s = q.trim().toLowerCase().replace(/^\./, '');
        if (s) l = l.filter((t) => t.tld.includes(s) || String(t.tld_unicode).toLowerCase().includes(s));
        return l;
    }, [catalog, filters, q]);

    const native = filters.language ? list.filter((t) => t.is_idn && (catalog?.languages?.[filters.language]?.native || []).includes(t.tld)) : [];
    const tableRows = filters.language ? list.filter((t) => !native.includes(t)) : list;
    const langLabel = filters.language ? t(catalog?.languages?.[filters.language]?.label) : null;

    const columns = [
        {
            title: t('Suffix'),
            key: 'tld',
            render: (_, item) => (
                <span style={{ fontWeight: 600 }}>
                    .{item.tld_unicode}
                    {item.is_featured && <Tag color="blue" style={{ marginLeft: 8 }}>{t('Popular')}</Tag>}
                    {item.is_idn && item.tld_unicode !== item.tld && <span style={{ marginLeft: 6, fontWeight: 400, fontSize: 11, color: 'var(--text-3)' }}>{item.tld}</span>}
                </span>
            ),
        },
        {
            title: t('First year'),
            dataIndex: 'registration_price',
            align: 'right',
            sorter: (a, b) => (a.registration_price ?? 1e9) - (b.registration_price ?? 1e9),
            render: (v, item) => {
                if (item.available === false) return <span style={{ color: 'var(--text-3)' }}>{t('Not yet available')}</span>;
                if (item.promo && Number(item.promo.price) < Number(v)) {
                    return (
                        <span>
                            <span style={{ color: '#cf1322', fontSize: 12, marginRight: 4 }}>{t('Special offer')}</span>
                            {formatMoney(item.promo.price, item.currency)}
                            <span style={{ color: 'var(--text-3)', textDecoration: 'line-through', marginLeft: 4, fontSize: 12 }}>{formatMoney(v, item.currency)}</span>
                        </span>
                    );
                }
                return formatMoney(v, item.currency);
            },
        },
        {
            title: t('Renewal/year'),
            dataIndex: 'renewal_price',
            align: 'right',
            sorter: (a, b) => (a.renewal_price ?? 1e9) - (b.renewal_price ?? 1e9),
            render: (v, t) => (
                <span style={{ color: v != null && Number(v) > Number(t.registration_price) + 0.001 ? '#d46b08' : undefined }}>
                    {formatMoney(v, t.currency)}
                </span>
            ),
        },
        ...(mobile ? [] : [{
            title: t('Transfer in'),
            dataIndex: 'transfer_price',
            align: 'right',
            sorter: (a, b) => (a.transfer_price ?? 1e9) - (b.transfer_price ?? 1e9),
            render: (v, t) => formatMoney(v, t.currency),
        }]),
        {
            title: '',
            key: 'op',
            width: mobile ? 56 : 90,
            align: 'right',
            render: (_, item) => (
                <Button size="small" type="link" icon={<SearchOutlined />} disabled={item.available === false} onClick={() => onPick?.(item.tld)}>
                    {mobile ? '' : t('Search')}
                </Button>
            ),
        },
    ];

    return (
        <div>
            <div className="ds-hero">
                <Input
                    allowClear
                    prefix={<SearchOutlined />}
                    placeholder={t('Search TLDs, e.g., com, io, 中国')}
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    style={{ maxWidth: 360 }}
                />
                <FilterBar catalog={catalog} filters={filters} onChange={onFiltersChange} count={list.length} />
            </div>

            {native.length > 0 && (
                <>
                    <div className="ds-section-title">{t('{{langLabel}} TLDs', { langLabel })}<span className="ds-sub">{t('The suffix itself is in {{langLabel}}', { langLabel })}</span></div>
                    <div className="ds-native-chips">
                        {native.map((item) => (
                            <Tag key={item.tld} color={item.available === false ? 'default' : 'blue'} onClick={() => item.available !== false && onPick?.(item.tld)}>
                                .{item.tld_unicode} · {item.available === false ? t('Not yet available') : formatMoney(item.registration_price, item.currency)}
                            </Tag>
                        ))}
                    </div>
                </>
            )}
            {filters.language && (
                <div className="ds-section-title">{t('TLDs supporting {{langLabel}} domain names', { langLabel })}<span className="ds-sub">{t('The domain name can use {{langLabel}}', { langLabel })}</span></div>
            )}
            <Table
                style={{ marginTop: filters.language ? 0 : 16 }}
                rowKey="tld"
                size="middle"
                loading={loading}
                dataSource={tableRows}
                columns={columns}
                pagination={{ pageSize: 20, showSizeChanger: false, size: 'small', showTotal: (n) => t('{{n}} total', { n: n }) }}
            />
        </div>
    );
};

export default TldPriceTable;
